/**
 * LeafSource Homepage Animations
 * GSAP ScrollTrigger-based animations — namespaced under window.LS
 * Handles section:load/unload for Shopify Theme Editor compatibility
 */
(function () {
  'use strict';

  /* ── Reduced motion check ── */
  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── Defaults ── */
  var DEFAULTS = {
    duration: 0.8,
    ease: 'power2.out',
    stagger: 0.12
  };

  /* ── Store all ScrollTrigger instances per section ── */
  var sectionTriggers = {};

  function storeTrigger(sectionId, trigger) {
    if (!sectionTriggers[sectionId]) sectionTriggers[sectionId] = [];
    sectionTriggers[sectionId].push(trigger);
  }

  /* ── fadeInUp ── */
  function fadeInUp(elements, trigger, opts, sectionId) {
    if (prefersReducedMotion) { gsap.set(elements, { opacity: 1, y: 0 }); return null; }
    opts = opts || {};
    var anim = gsap.from(elements, {
      y: opts.y || 40,
      opacity: 0,
      duration: DEFAULTS.duration,
      ease: DEFAULTS.ease,
      stagger: opts.stagger != null ? opts.stagger : DEFAULTS.stagger,
      delay: opts.delay || 0,
      scrollTrigger: {
        trigger: trigger,
        start: 'top 80%',
        toggleActions: 'play none none none'
      }
    });
    if (sectionId && anim.scrollTrigger) storeTrigger(sectionId, anim.scrollTrigger);
    return anim;
  }

  /* ── staggerCards ── */
  function staggerCards(container, childSelector, opts, sectionId) {
    if (!container) return null;
    if (prefersReducedMotion) {
      var els = typeof container === 'string'
        ? document.querySelectorAll(container + ' ' + childSelector)
        : container.querySelectorAll(childSelector);
      gsap.set(els, { opacity: 1, y: 0 });
      return null;
    }
    opts = opts || {};
    var children = typeof container === 'string'
      ? document.querySelectorAll(container + ' ' + childSelector)
      : container.querySelectorAll(childSelector);

    var anim = gsap.from(children, {
      y: 40,
      opacity: 0,
      duration: DEFAULTS.duration,
      ease: DEFAULTS.ease,
      stagger: opts.stagger || 0.15,
      scrollTrigger: {
        trigger: container,
        start: 'top 80%',
        toggleActions: 'play none none none'
      }
    });
    if (sectionId && anim.scrollTrigger) storeTrigger(sectionId, anim.scrollTrigger);
    return anim;
  }

  /* ── scaleIn ── */
  function scaleIn(elements, trigger, opts, sectionId) {
    if (prefersReducedMotion) { gsap.set(elements, { opacity: 1, scale: 1 }); return null; }
    opts = opts || {};
    var anim = gsap.from(elements, {
      scale: 0.9,
      opacity: 0,
      duration: DEFAULTS.duration,
      ease: DEFAULTS.ease,
      stagger: opts.stagger || 0.1,
      scrollTrigger: {
        trigger: trigger,
        start: 'top 80%',
        toggleActions: 'play none none none'
      }
    });
    if (sectionId && anim.scrollTrigger) storeTrigger(sectionId, anim.scrollTrigger);
    return anim;
  }

  /* ── slideIn ── */
  function slideIn(elements, trigger, opts, sectionId) {
    if (prefersReducedMotion) { gsap.set(elements, { opacity: 1, x: 0 }); return null; }
    opts = opts || {};
    var x = opts.direction === 'right' ? 60 : -60;
    var anim = gsap.from(elements, {
      x: x,
      opacity: 0,
      duration: DEFAULTS.duration,
      ease: DEFAULTS.ease,
      stagger: opts.stagger || 0.2,
      scrollTrigger: {
        trigger: trigger,
        start: 'top 80%',
        toggleActions: 'play none none none'
      }
    });
    if (sectionId && anim.scrollTrigger) storeTrigger(sectionId, anim.scrollTrigger);
    return anim;
  }

  /* ── heroTextReveal ── */
  function heroTextReveal(container, sectionId) {
    if (prefersReducedMotion) {
      gsap.set(container.querySelectorAll('.word'), { opacity: 1, y: 0 });
      return null;
    }
    var words = container.querySelectorAll('.word');
    var tl = gsap.timeline();
    tl.from(words, {
      y: 30,
      opacity: 0,
      duration: 0.6,
      ease: 'power2.out',
      stagger: 0.05
    });

    var subcopy = container.querySelector('[data-hero-subcopy]');
    if (subcopy) {
      tl.from(subcopy, { y: 20, opacity: 0, duration: 0.6, ease: 'power2.out' }, '-=0.3');
    }

    var cta = container.querySelector('[data-hero-cta]');
    if (cta) {
      tl.from(cta, { y: 20, opacity: 0, duration: 0.5, ease: 'power2.out' }, '-=0.3');
    }

    return tl;
  }

  /* ── navScrollEffect ── */
  function navScrollEffect(navElement, sectionId) {
    if (prefersReducedMotion) return null;
    var st = ScrollTrigger.create({
      start: 'top top',
      onUpdate: function (self) {
        var currentY = window.scrollY;
        if (currentY > 40) {
          navElement.classList.add('nav-scrolled');
        } else {
          navElement.classList.remove('nav-scrolled');
        }
        if (self.direction === 1 && currentY > 200) {
          navElement.classList.add('nav-hidden');
        } else if (self.direction === -1) {
          navElement.classList.remove('nav-hidden');
        }
      }
    });
    if (sectionId) storeTrigger(sectionId, st);
    return st;
  }

  /* ── killSection: cleanup ScrollTriggers for a section ── */
  function killSection(sectionId) {
    if (sectionTriggers[sectionId]) {
      sectionTriggers[sectionId].forEach(function (st) {
        if (st && st.kill) st.kill();
      });
      delete sectionTriggers[sectionId];
    }
  }

  /* ── killAll ── */
  function killAll() {
    Object.keys(sectionTriggers).forEach(function (key) {
      killSection(key);
    });
  }

  /* ── Announcement bar rotation ── */
  function announcementRotation(container) {
    var textEl = container.querySelector('[data-announcement-text]');
    var messages = JSON.parse(container.getAttribute('data-announcements') || '[]');
    if (!messages.length || !textEl) return null;

    var currentIndex = 0;
    var intervalId = null;
    var isPaused = false;

    function animateTo(direction) {
      if (!textEl) return;
      gsap.to(textEl, {
        opacity: 0,
        duration: 0.5,
        ease: 'power2.inOut',
        onComplete: function () {
          currentIndex = direction === 'next'
            ? (currentIndex + 1) % messages.length
            : (currentIndex - 1 + messages.length) % messages.length;
          textEl.textContent = messages[currentIndex];
          gsap.to(textEl, { opacity: 1, duration: 0.5, ease: 'power2.inOut' });
        }
      });
    }

    function start() {
      intervalId = setInterval(function () {
        if (!isPaused) animateTo('next');
      }, 4000);
    }

    function stop() {
      if (intervalId) clearInterval(intervalId);
    }

    var prevBtn = container.querySelector('[data-announcement-prev]');
    var nextBtn = container.querySelector('[data-announcement-next]');
    if (prevBtn) prevBtn.addEventListener('click', function () { animateTo('prev'); stop(); start(); });
    if (nextBtn) nextBtn.addEventListener('click', function () { animateTo('next'); stop(); start(); });

    container.addEventListener('mouseenter', function () { isPaused = true; });
    container.addEventListener('mouseleave', function () { isPaused = false; });
    container.addEventListener('focusin', function () { isPaused = true; });
    container.addEventListener('focusout', function () { isPaused = false; });

    start();
    return { stop: stop };
  }

  /* ── Testimonial rotation ── */
  function testimonialRotation(container) {
    var grid = container.querySelector('[data-testimonial-grid]');
    var scrollSnap = container.querySelector('[data-testimonial-scroll]');
    var dots = container.querySelectorAll('[data-testimonial-dot]');
    var prevBtns = container.querySelectorAll('[data-testimonial-prev]');
    var nextBtns = container.querySelectorAll('[data-testimonial-next]');
    var allCards = JSON.parse(container.getAttribute('data-testimonials') || '[]');
    var perPage = 3;
    var totalPages = Math.ceil(allCards.length / perPage);
    var activePage = 0;
    var isPaused = false;
    var intervalId = null;

    function renderPage() {
      if (grid) {
        var start = activePage * perPage;
        var pageCards = allCards.slice(start, start + perPage);
        var html = '';
        pageCards.forEach(function (t) {
          html += '<div class="ls-testimonial-card flex flex-col rounded-2xl border border-forest/8 bg-sage-light p-8 animate-fade-in">';
          html += '<div class="flex gap-0.5">';
          for (var s = 0; s < t.rating; s++) {
            html += '<svg viewBox="0 0 20 20" fill="currentColor" class="h-4 w-4 text-gold"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>';
          }
          html += '</div>';
          html += '<blockquote class="mt-5 flex-1"><p class="text-base leading-relaxed text-text lg:text-lg">&ldquo;' + t.quote + '&rdquo;</p></blockquote>';
          html += '<div class="mt-6 border-t border-forest/8 pt-5">';
          html += '<p class="text-sm font-semibold text-forest">' + t.name + '</p>';
          html += '<p class="text-xs text-text-light">' + t.location + ' &middot; ' + t.product + '</p>';
          html += '</div></div>';
        });
        grid.innerHTML = html;
      }

      dots.forEach(function (dot, i) {
        if (i === activePage) {
          dot.classList.add('w-6', 'bg-forest');
          dot.classList.remove('w-2', 'bg-forest/25');
        } else {
          dot.classList.remove('w-6', 'bg-forest');
          dot.classList.add('w-2', 'bg-forest/25');
        }
      });

      if (scrollSnap) {
        var firstCard = scrollSnap.firstElementChild;
        if (firstCard) {
          var cardWidth = firstCard.getBoundingClientRect().width;
          var targetIndex = activePage * perPage;
          scrollSnap.scrollTo({ left: targetIndex * (cardWidth + 16), behavior: 'smooth' });
        }
      }
    }

    function goTo(page) {
      activePage = ((page % totalPages) + totalPages) % totalPages;
      renderPage();
    }

    prevBtns.forEach(function (btn) { btn.addEventListener('click', function () { goTo(activePage - 1); resetInterval(); }); });
    nextBtns.forEach(function (btn) { btn.addEventListener('click', function () { goTo(activePage + 1); resetInterval(); }); });
    dots.forEach(function (dot, i) { dot.addEventListener('click', function () { goTo(i); resetInterval(); }); });

    container.addEventListener('mouseenter', function () { isPaused = true; });
    container.addEventListener('mouseleave', function () { isPaused = false; });

    function resetInterval() {
      if (intervalId) clearInterval(intervalId);
      intervalId = setInterval(function () { if (!isPaused) goTo(activePage + 1); }, 6000);
    }

    resetInterval();
    renderPage();

    return { stop: function () { if (intervalId) clearInterval(intervalId); } };
  }

  /* ── AJAX Add to Cart ── */
  function ajaxAddToCart(button, variantId, quantity) {
    if (button.disabled) return;
    var originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding...';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: quantity || 1 }] })
    })
    .then(function (res) {
      if (!res.ok) throw new Error('Add to cart failed');
      return res.json();
    })
    .then(function () {
      button.textContent = 'Added!';
      /* Update cart count in header */
      fetch('/cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var counters = document.querySelectorAll('[data-cart-count]');
          counters.forEach(function (c) { c.textContent = cart.item_count; });
          /* Try to open cart drawer if theme supports it */
          var drawer = document.querySelector('.js-ajax-cart-drawer, #right-drawer, [data-cart-drawer]');
          if (drawer) {
            drawer.classList.add('active', 'is-open');
            document.body.classList.add('drawer-open');
          }
        });
      setTimeout(function () {
        button.textContent = originalText;
        button.disabled = false;
      }, 1500);
    })
    .catch(function () {
      button.textContent = 'Error';
      setTimeout(function () {
        button.textContent = originalText;
        button.disabled = false;
      }, 2000);
    });
  }

  /* ── Video toggle for Meet the Founder ── */
  function founderVideoToggle(container) {
    var video = container.querySelector('video');
    var overlay = container.querySelector('[data-play-overlay]');
    if (!video) return;

    var isPlaying = false;

    container.addEventListener('click', function () {
      if (!isPlaying) {
        video.muted = false;
        video.currentTime = 0;
        video.loop = false;
        video.play();
        isPlaying = true;
        if (overlay) overlay.style.opacity = '0';
        if (overlay) overlay.style.pointerEvents = 'none';
      } else {
        video.pause();
        video.muted = true;
        video.loop = true;
        video.play();
        isPlaying = false;
        if (overlay) overlay.style.opacity = '1';
        if (overlay) overlay.style.pointerEvents = '';
      }
    });

    video.addEventListener('ended', function () {
      video.muted = true;
      video.loop = true;
      video.play();
      isPlaying = false;
      if (overlay) overlay.style.opacity = '1';
      if (overlay) overlay.style.pointerEvents = '';
    });
  }

  /* ── Horizontal scroll arrows (Shop by Concern) ── */
  function scrollArrows(container) {
    var track = container.querySelector('[data-scroll-track]');
    var prevBtn = container.querySelector('[data-scroll-prev]');
    var nextBtn = container.querySelector('[data-scroll-next]');
    if (!track) return;

    var cards = track.querySelectorAll('[data-animate="card"]');
    var padding = parseFloat(getComputedStyle(track).scrollPaddingLeft) || 0;

    function check() {
      if (prevBtn) prevBtn.disabled = track.scrollLeft < 3;
      if (nextBtn) nextBtn.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 3;
    }

    /* Find the first card whose left edge is past the current snap edge */
    function nextCard() {
      var edge = track.scrollLeft + padding + 3;
      for (var i = 0; i < cards.length; i++) {
        if (cards[i].offsetLeft > edge) return cards[i];
      }
      return null;
    }

    /* Find the last card whose left edge is before the current snap edge */
    function prevCard() {
      var edge = track.scrollLeft + padding - 3;
      for (var i = cards.length - 1; i >= 0; i--) {
        if (cards[i].offsetLeft < edge) return cards[i];
      }
      return null;
    }

    track.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', function () { padding = parseFloat(getComputedStyle(track).scrollPaddingLeft) || 0; check(); });
    check();

    if (prevBtn) prevBtn.addEventListener('click', function () {
      var card = prevCard();
      if (card) track.scrollTo({ left: card.offsetLeft - padding, behavior: 'smooth' });
    });
    if (nextBtn) nextBtn.addEventListener('click', function () {
      var card = nextCard();
      if (card) track.scrollTo({ left: card.offsetLeft - padding, behavior: 'smooth' });
    });
  }

  /* ── Public API ── */
  window.LS = {
    DEFAULTS: DEFAULTS,
    fadeInUp: fadeInUp,
    staggerCards: staggerCards,
    scaleIn: scaleIn,
    slideIn: slideIn,
    heroTextReveal: heroTextReveal,
    navScrollEffect: navScrollEffect,
    killSection: killSection,
    killAll: killAll,
    announcementRotation: announcementRotation,
    testimonialRotation: testimonialRotation,
    ajaxAddToCart: ajaxAddToCart,
    founderVideoToggle: founderVideoToggle,
    scrollArrows: scrollArrows,
    prefersReducedMotion: prefersReducedMotion
  };

})();
